import React, { useCallback, useEffect, useRef, useState, useLayoutEffect, useMemo } from 'react'
import { Grid } from '@material-ui/core'

import { useDispatch, useSelector } from 'react-redux'
import { navigate } from '@reach/router'
import { Button } from '@refera/ui-web'
import Theme from '@refera/ui-core'

import { getProvidersList } from '_modules/provider/actions'
import {
  providersSelector,
  getProvidersLoadingSelector,
  providersResultSelector,
} from '_modules/provider/selectors'
import { ROUTES } from '_utils/constants'
import { getCitiesOptions } from '_modules/utils/selectors'
import { getCities } from '_modules/utils/actions'
import { categoryOptionSelector } from '_modules/categories/selectors'
import { getCategories } from '_modules/categories/actions'
import { usePrevious } from '_hooks/use-previous'

import useStyles from './styles'
import HeaderTitle from '_/components/header-title'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import useRolePermission from '_/hooks/use-role-permission'
import { FilterSearch as FilterIcon } from '@refera/ui-icons'
import useToggle from '_hooks/use-toggle'
import ProviderFilter from '_views/manage-providers/filters'
import DataTable from '_views/manage-providers/data-table'
import { ProviderSolicitationModal } from './components/solicitationModal'

const ManageProviders = () => {
  const styles = useStyles()

  const dispatch = useDispatch()

  const { count } = useSelector(providersSelector)
  const results = useSelector(providersResultSelector)
  const providersIsLoading = useSelector(getProvidersLoadingSelector)
  const wasLoading = usePrevious(providersIsLoading)
  const cityOptions = useSelector(getCitiesOptions)
  const serviceOptions = useSelector(categoryOptionSelector)
  const refs = useRef({})
  const [, setEllipsisCell] = useState([])
  const { checkIfUserDoesNotPermission, isSAAS } = useRolePermission()
  const [isFilterOpen, handleFilterOpen] = useToggle()
  const [openProviderSolicitationModal, setOpenProviderSolicitationModal] = useState(false)

  const [filterValues, setFilterValues] = useState({
    origin: '',
    tradesman_city: '',
    tradesman_work_city: [],
    category: [],
  })

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [orderBy, setOrderBy] = useState('name')

  const handleOrderBy = useCallback(orderObj => {
    const order = orderObj?.[0]
    if (!order) {
      setOrderBy('name')
      return
    }

    const { field, sort } = order
    const mapField =
      {
        nome: 'name',
        responsavel: 'personResponsibleName',
        cidade: 'city',
        data_cadastro: 'providerSignupDate',
        statusLabel: 'registrationStatus',
        minhaAvaliacao: 'rating',
        etapaLabel: 'onboardingStage',
        whatsapp: 'phone',
      }[field] || 'name'

    setOrderBy(`${sort === 'desc' ? '-' : ''}${mapField}`)
  }, [])

  const handleApplyFilter = useCallback(data => {
    setFilterValues({
      origin: data.origin || '',
      tradesman_city: data.tradesman_city || '',
      tradesman_work_city: Array.isArray(data.tradesman_work_city) ? data.tradesman_work_city : [],
      category: Array.isArray(data.category) ? data.category : [],
    })
    setPage(0)
  }, [])

  const handleService = useCallback(() => {
    dispatch(getCategories())
  }, [dispatch])

  const handleCity = useCallback(() => {
    if (!cityOptions.length) {
      dispatch(getCities()).then(() => {
        handleService()
      })
    }
  }, [cityOptions.length, dispatch])

  useEffect(() => {
    const filtersPayload = {
      ...filterValues,
      category: Array.isArray(filterValues.category)
        ? filterValues.category.map(item => item.value).join(',')
        : filterValues.category || '',
      tradesman_work_city: Array.isArray(filterValues.tradesman_work_city)
        ? filterValues.tradesman_work_city.map(item => item.value).join(',')
        : filterValues.tradesman_work_city || '',
    }

    dispatch(
      getProvidersList({
        mainTradesman: true,
        page: page + 1,
        pageSize,
        orderBy,
        ...filtersPayload,
      })
    ).then(() => {
      handleCity()
    })
  }, [dispatch, page, pageSize, orderBy, filterValues])

  const handleTableRowClick = useCallback(params => {
    if (
      checkIfUserDoesNotPermission(PERMISSIONS.TRADESMAN_CONFIGS, [
        PERMISSIONS_ACTIONS.READ,
        PERMISSIONS_ACTIONS.EDIT,
        PERMISSIONS_ACTIONS.ADD,
      ])
    ) {
      return null
    }

    return navigate(`${ROUTES.PROVIDER}/${params.row.id}`)
  }, [])

  const checkEmailFieldEllipsis = useCallback(() => {
    const ellipsisCellState = Object.keys(refs.current).map(item => ({
      id: item,
      isEllipsis: refs.current[item]?.offsetWidth < refs.current[item]?.scrollWidth,
    }))

    setEllipsisCell(ellipsisCellState)
  }, [refs])

  useLayoutEffect(() => {
    if (Object.values(refs.current).length > 0 && !providersIsLoading && wasLoading) {
      checkEmailFieldEllipsis()
    }
    window.addEventListener('resize', checkEmailFieldEllipsis)

    return () => window.removeEventListener('resize', checkEmailFieldEllipsis)
  }, [checkEmailFieldEllipsis, providersIsLoading, wasLoading])

  const renderSaasButtons = useMemo(() => {
    if (isSAAS) {
      return (
        <Button onClick={() => setOpenProviderSolicitationModal(true)}>Solicitar Prestador</Button>
      )
    }
    return null
  }, [isSAAS])

  return (
    <Grid container className={styles.container}>
      <ProviderFilter
        handleFilterOpen={handleFilterOpen}
        open={isFilterOpen}
        cityOptions={cityOptions}
        categoryOptions={serviceOptions}
        onApplyFilter={handleApplyFilter}
        defaultValues={filterValues}
      />
      <HeaderTitle title="Gestão de Prestadores">
        <div className={styles.actionButtons}>
          {renderSaasButtons}
          <Button onClick={() => navigate(ROUTES.LEAD_PROVIDERS)}>Base da Refera</Button>
        </div>
        <Button
          variant="ghost"
          startIcon={<FilterIcon color={Theme.Colors.Primary.Base} />}
          onClick={handleFilterOpen}
        >
          Filtros
        </Button>
      </HeaderTitle>
      <DataTable
        dataToShow={results}
        count={count}
        onRowClick={handleTableRowClick}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSortModelChange={handleOrderBy}
        loading={providersIsLoading}
      />
      <ProviderSolicitationModal
        isOpen={openProviderSolicitationModal}
        setIsOpen={setOpenProviderSolicitationModal}
      />
    </Grid>
  )
}

export default ManageProviders
